import {IFileDocument} from '../shared.model';
import {AdminMandant} from '../cloud/cloud.model';

export interface IConnectedContacts {
  Id: number;
  CountryText: string;
  Email: string;
  FirstName: string;
  LastName: string;

  /** Frontend future */
  extended?: boolean;
}

export interface LeadsData {
  Highlights?: {
    [key: string]: string[]
  };
  Lead: LeadDataObject;
  SuggestedAction?: LeadsDataAction;
  Actions?: LeadsDataAction[];
  DoneDate?: Date | string;
  ConnectedContacts?: IConnectedContacts[];
  IsAppointment?: boolean;
  isDuplicate?: boolean;
  requiresConfirmation?: boolean;
  Messages?: LeadTakenMessage[]
}

export interface LeadTakenMessage {
  Id: number;
  Date: Date | string;
  Read: boolean;
  Sender: {
    FirstName: string;
    LastName: string;
  };
}

export interface LeadsDataAction {
  Id: number;
  IconURL?: string;
  Label?: string;
  Name?: string;
  Hidden?: boolean;
  Suggested?: boolean;

  // frontend specific
  actionIconUrl?: string;
}

export interface ICompanyContacts {
  Address: string;
  Anonymized: boolean;
  City: string;
  Company: string;
  ContactMethodId: number;
  ContactTypeId: number;
  CountryCode: string;
  CountryText: string;
  Email: string;
  FirstName: string
  Id: number;
  Inactive: boolean;
  LanguageCode: string;
  LastName: string;
  Mobile: string;
  MobilePrefix: string;
  Phone: string;
  PhonePrefix: string;
  ZipCode: string;
  ManagementLeadId?: number;
}

export interface IContactDescription {
  Id: number;
  Email?: string;
  FirstName?: string;
  LastName?: string;
  CountryText?: string;
}

export interface IUser {
  Id: number;
  FirstName?: string;
  LastName?: string;
  Roles?: string[];
  Email?: string;
  Login?: string;
  Phone?: string;
  Mobile?: string;
  Available?: boolean;
  AvatarURL?: string;
  LanguageCode?: string;
  EnableReporting?: boolean;
  Inactive?: boolean;
  LastActivity?: string;
  Organisation?: string;
  LeadMailEnabled?: boolean;
  NotificationsEnabled?: boolean;
  AppointmentMailEnabled?: boolean;
  ReminderMailEnabled?: boolean;
  GvlMailEnabled?: boolean;
  ExternalMailCCEnabled?: boolean;
  CompletedMailEnabled?: boolean;
  Studios?: string[];
  Gender?: string;
  Absence?: IAbsence;
}

export interface IAbsence {
  Until: Date | string;
  AbsenceTypeId: number;
  AbsenceTypeText: string;
  Substitute: IUser;
}

export interface LeadData {
  Lead: LeadDataObject;
  IsAppointment?: boolean;
  SuggestedAction?: LeadsDataAction;
  Actions?: LeadsDataAction[];
  ConnectedContacts?: IContactDescription[];
  CompanyContacts?: ICompanyContacts[];
  Contact?: IContact;
  Referrer?: IUser;
  Editor?: IUser;
  Creator: IUser;
  AssignmentDate?: Date | string;
  DoneDate?: Date | string;
  ExportURL?: string;
  AllLeads?: LeadDataObject[];
  ConnectedLeads?: LeadDataObject[];
  PrivacyDate?: Date | string;
  PrivacyText?: string;
  Keywords?: Keyword[];
  PrivacyDocument?: LeadDocumentsModel;
  ExternalEmail?: string;
  ExternalDesc?: string;
  Events?: LeadEventsModel[];
  EventAttendees?: LeadEventsAttedeesModel[];
  CanDelete?: boolean;
  ConstructionTypeId?: number;
  Articles?: Articles[];
  BrokeringInfo?: LeadDataBrokeringInfo;
  BrokeredLeads?: BrokeredLeadDataObject[];

  // frontend specific
  actionIconUrl?: string;
}

export interface LeadDataBrokeringInfo {
  CanAccept: boolean;
  FirstAcceptedDate: Date | string;
  LastAcceptedDate: Date | string;
  NumberAccepted: number;
  // Set on a brokered lead: the id of the lead it was brokered from
  SourceLeadId?: number;
}

export interface BrokeredLeadDataObject extends Omit<LeadDataObject, 'Address' | 'Contact'> {
  Address?: IAddressData;
  Contact?: BrokeredLeadContactObject;
  BrokeringPoolId?: number;
  PremiumCustomer?: boolean;
  BrokeringFailed?: boolean;
  BrokeringSuccess?: boolean;
}

export interface LeadDataInteractionObject {
  Id: number;
  AnfrageId: number;
  AktionId: number;
  Notiz: string;
  BearbeiterId: number;
  AnhangDokumentId: number;
  Termin: string;
  TerminKlasseId: number;
  RejectKlasseId: number;
  KeinInteresseKlasse: number;
  AnfragetypId: number;
  StatusId: number;
  NeuerBearbeiterId: number;
  AnfrageText: string;
  Deadline: string;
  AnfrageNummer: string;
  AktionName: string;
  AktionText: string;
  TerminKlasseText: string;
  RejectKlasseText: string;
  AnfrageTypText: string;
  StatusText: string;
  NeuerBearbeiterName: string;
  CreTs: string;
  CreUser: string;
  CreApp: string;
}

export interface IAddressData {
  CountryCode?: string;
  ZipCode?: string;
  Commission?: string;
  Address?: string;
  AddressExt?: string;
  City?: string;
  Email?: string;
  Phone?: string;
  PhonePrefix?: string;
  Mobile?: string;
  MobilePrefix?: string;
  ProvinceCode?: string;
  ProvinceText?: string;
  Website?: string;
}

export interface LeadDataObject {
  Id: number;
  Number?: string;
  DateFormattedId?: string;
  LeadTypeId: number;
  StatusId: number;
  EditorId: number;
  CreatorId: number;
  LeadText?: string;
  Deadline?: string;
  LeadSourceId: number;
  Contact?: LeadsDataContactObject;
  Commission?: string;
  LeadTypeText?: string;
  StatusText?: string;
  DoneDate?: string;
  IsShared?: boolean;
  Done: boolean;
  SerialNumber?: string;
  InvoiceNumber?: string;
  LeadSourceText?: string;
  ReferrerUrl?: string;
  SourceDomain?: string;
  UTMParameters?: { [key: string]: string };
  EditorName?: string;
  CreatorName?: string;
  LastEditorName?: string;
  LastEditorId?: number;
  Address: IAddressData;
  Read: boolean;
  LastChange: string;
  LastAction: string;
  VisibilityRule: string;
  CreTS: string;
  ModTS?: string;
  SortOrderType?: number;
  SortOrderStatus?: number;
  LimitedAccess: boolean
  ConstructionTypeId?: number;
  ExternalEmail?: string;
  ExternalDesc?: string;
  MandantDesc?: string;
  RequiresConfirmation?: boolean;
  ConsentAccredation?: boolean;
  BrokeringPoolId?: number;
  PremiumCustomer?: boolean;

  // frontend specific
  isBrokeredLead?: boolean;
}

export interface BrokeredLeadContactObject extends Partial<LeadsDataContactObject> {
  Id: number;
  Email: string;
  FirstName: string;
  LastName: string;
  Latitude?: number;
  Longitude?: number;
  CountryCode?: string;
  CountryText?: string;
  ProvinceText?: string;
  SalutationText?: string;
  Address?: string;
  ZipCode?: string;
  City?: string;
  Phone?: string;
  Anonymized?: boolean;
  LanguageId?: number;
  ContactTypeId?: number;
  ContactMethodId?: number;
  Inactive?: boolean;
}

export interface LeadsDataContactObject {
  ExternalEmail: string;
  Address: string;
  City: string;
  Comment?: string;
  ContactMethodId: number;
  ContactMethodName: string;
  ContactMethodText: string;
  ContactTypeId: number;
  ContactTypeText: string;
  CountryCode: string;
  CountryText: string;
  Email: string;
  FirstName: string;
  Id: number;
  KKZ?: any;
  LanguageCode: string;
  LanguageText: string;
  LastName: string;
  Mobile?: string;
  Phone?: string;
  ZipCode?: string;
  PhonePrefix?: string;
  ProvinceText?: string;
  MobilePrefix?: string;
  Anonymized?: boolean
  AdditionalEmail?: string;
  Company?: string;
  ManagementLeadId?: number;
}

export interface LeadDataMediaFileObject {
  ID: number;
  Data: string;
  Filename: string;
  Extension: string;
  Description: string;
  Changed: boolean;
  Filedate: string;
  CreTs: string;
  ModTs: string;
  CreUser: string;
  ModUser: string;
  CreApp: string;
  ModApp: string;
}

export interface LeadDataPhotoObject {
  ID: number;
  Data: string;
  Filename: string;
  Extension: string;
  Description: string;
  Changed: boolean;
  Filedate: string;
  CreTs: string;
  ModTs: string;
  CreUser: string;
  ModUser: string;
  CreApp: string;
  ModApp: string;
}

export interface LeadDataContactNextStepAction {
  Id: number;
  Name: string;
  Text: string;
  InfoText: string;
  ButtonText: string;
  IconDocumentId: number;
  IconTimestamp: string;
  RequiredRight: string;
  RequiredTransitiveRight: string;
  SuccessStatusCode: number;
}

export interface LeadDataContactNextStepStepObject {
  Id: number;
  StatusId: number;
  Order: number;
  ActionId: number;
  IsSuggested: boolean;
  ShowOwner: boolean;
  ShowPrevOwner: boolean;
  ShowOthers: boolean;
}

export interface LeadDataUserObject {
  Id: number;
  LoginName: string;
  OrganizationId: number;
  Photo: LeadDataPhotoObject;
  MandantId: number;
  Name: string;
  Email: string;
  Phone: string;
  LngId: number;
  OrganizationName: string;
}

export interface LeadDataDocumentObject {
  Id: number;
  InteractionsId: number;
  Document: LeadDataMediaFileObject;
  IsDeleted: boolean;
  AnfrageId: number;
  CreTs: string;
  CreUser: string;
  CreApp: string;
  ModTs: string;
  ModUser: string;
  ModApp: string;
}


export interface ILeadContact {
  Id?: string;
  ContactTypeId: number;
  ContactMethodId: number;
  FirstName: string;
  LastName: string;
  CountryCode: string;
  ZipCode: string;
  Address: string;
  AddressExt: string;
  City: string;
  Email: string;
  AdditionalEmail: string;
  Phone: string;
  Mobile: string;
  PhonePrefix: string;
  MobilePrefix: string;
  ProvinceCode: string;
  Website: string;
  LanguageCode: string;
  Comment?: string;
  Company: string;
  JobTitle: string;
  VatNumber: string;
  KKZ?: string;
}


export interface CreateLead {
  Contact: ILeadContact;
  LeadTypeId: number;
  LeadSourceId: number;
  LeadText?: string;
  Commission?: string;
  PrivacyDocument?: IFileDocument;
  PrivacyDate?: string;
  PrivacyText?: string;
  LeadAddress?: IAddressData;
  Articles?: Articles[]
}


export interface LeadType {
  Id: number;
  Name: string;
  Description: string;
  Inactive?: boolean;
  SortOrder: number;
  IsGeneral: boolean;
}

export interface DeadlineStatusOption {
  Id: string;
  Text: string;
}

export interface LeadStatus {
  Id: number;
  Success: boolean;
  Initial: boolean;
  Completed: boolean;
  DeadlineHours: number;
  DeadlineSuccessorId: number;
  SortOrder: number;
  Text: string;
}

export interface LeadSources {
  Id: number;
  Name: string;
  Description: string;
  FinstralOnly: boolean;
  Inactive: boolean;
  SortOrder: number;
}


export interface IContact {
  ConsentMarketing?: boolean;
  ConsentThirdParties?: boolean;
  ExternalEmail: string;
  Id?: string;
  ContactId?: number;
  ContactTypeId: number;
  ContactMethodId: number;
  FirstName: string;
  LastName: string;
  SalutationText: string;
  CountryCode: string;
  CountryText?: string;
  LanguageText?: string;
  Inactive?: boolean;
  ContactTypeText?: string;
  ContactMethodText?: string;
  ContactMethodName?: string;
  ZipCode: string;
  Address: string;
  AddressExt: string;
  City: string;
  Email: string;
  AdditionalEmail: string;
  Phone: string;
  PhonePrefix: string;
  PhoneOther: string;
  PhoneOtherPrefix: string;
  Mobile: string;
  MobilePrefix: string;
  LanguageCode: string;
  Comment: string;
  LeadTypeId: number;
  LeadText: string;
  Commission: string;
  LeadSourceId: number;
  PrivacySignature: {
    Id: number;
    Name: string;
    MimeType: string;
    Body: string;
    Filedate: number;
  };
  PrivacyPhoto: {
    Id: number;
    Name: string;
    MimeType: string;
    Body: string;
    Filedate: string;
  };
  PrivacyDate: string;
  CreTS: string | Date;
  ModTS: string | Date;
  JobTitle: string;
  Company: string;
  VatNumber: string;
  Website: string;
  KKZ?: string;
  ProvinceText?: string;
  ProvinceCode?: string;
  Anonymized: boolean;
  Title?: string;
  CustomerCode?: string,
  TaxNumber?: string,
  CustomerCodeComposer?: string,
}

export interface LeadsHistory {
  ExternalEmail: string;
  RejectReasonText: string;
  Id: number;
  LeadId: number;
  BrokeringId?: number;
  BrokeringTargets?: LeadHistoryBrokeringTarget[];
  ActionId: number;
  ComposerChanges: LeadComposerChangesModel[]
  Comment: string;
  LeadEditorId: number;
  AppointmentDate: string;
  AppointmentClassId: number;
  RejectClassId: number;
  NotInterestedTypeId: number;
  PriceDeviation?: string;
  NotInterestedDetailId?: number;
  LeadTypeId: number;
  LeadStateId: number;
  NewLeadEditorId: number;
  LeadText: string;
  Deadline: string;
  LeadNumber: string;
  ActionName: string;
  ActionText: string;
  LeadStateName: string;
  AppointmentClassText: string;
  RejectClassText: string;
  LeadTypeText: string;
  LeadStateText: string;
  CreTs: string;
  CreUser: string;
  Creator: LeadCreatorModel;
  LeadEditor: LeadCreatorModel;
  Documents: LeadDocumentsModel[];
  ConsentMarketing?: boolean;
  ConsentThirdParties?: boolean;
  AppointmentStudio?: LeadAppointmentStudio,
  AppointmentAddress?: LeadAppointmentAddress,
}

export interface LeadHistoryBrokeringTarget {
  LeadId: number;
  Wave: number;
  LeadNumber: string;
  DateFormattedId: string;
  LeadEditor: LeadCreatorModel;
  // Name of the partner company the lead was brokered to
  MandantName?: string;
}

export interface BrokeringTargetViewModel {
  target: LeadHistoryBrokeringTarget;
  displayId: string;
  leadEditor: string;
}

export interface LeadCreatorModel {
  Id: number;
  FirstName: string;
  LastName: string;
  Organisation: string;
  Email: string;
  Phone: string;
  LanguageCode: string;
  AvatarURL: string;
  CurrentAbsence?: CreatorCurrentAbsence;
  AppointmentMailEnabled?: boolean
  Available?: boolean;
  CompletedMailEnabled?: boolean;
  EnableReporting?: boolean;
  ExternalMailCCEnabled?: boolean;
  Inactive?: boolean
  LastActivity?: Date;
  LeadMailEnabled?: boolean
  Login?: string;
  NotificationsEnabled?: boolean;
  Studios?: string[];
}

export interface CreatorCurrentAbsence {
  Id: number;
  From: string;
  To: string;
  Active: boolean;
  Description: string;
}

export interface LeadEventsModel {
  Location: string;
  Name: string;
  Agency: string;
  PlannerKKZ: string;
}

export interface LeadEventsAttedeesModel {
  FirstName: string,
  LastName: string,
  Email: string
}

export interface LeadComposerChangesModel {
  Amount: number,
  Name: string,
  Creator: LeadCreatorModel,
  CurrencyCode: string,
  CurrencyText: string,
  Date: string,
  Id: number,
  IsOrder: boolean,
  IsHandedOver: boolean,
  IsManual: boolean,
  IsMain: boolean,
  IsSelected: boolean,
  LastChangedDate: string,
  Operation: string
}

export interface LeadAppointmentStudio {
  Address: string;
  Area: number;
  CertificationIFT: boolean;
  City: string;
  Country: string;
  CountryText: string;
  Email: string;
  KKZ: string;
  Name: string;
  PLZ: string;
}

export interface LeadAppointmentAddress {
  Address: string;
  City: string;
  CountryCode: string;
  ZipCode: string;
}

export interface LeadDocumentsModel {
  Id: number;
  Name: string;
  MimeType: string;
  Filedate: string;
  LeadId: number;
  LeadNumber: string;
  Creator: string;
  DownloadURL: string;
  ThumbnailURL: string;
  OpenURL: string;
  Deleted: boolean;
  Favorite: boolean;
}

export interface ILeadData {
  creator: IUser;
  editor?: IUser;
  creTS: string;
}

export interface CheckDuplicateContact {
  Lead: LeadDataObject;
  PrimaryConflict?: IContact;
  ImportedContact: IContact;
  SecondaryConflicts?: IContact[];
}

export interface SolveDuplicateContact {
  Mode: number,
  NewContact?: IContact;
  ContactsToReplace?: number[],
  ExistingContact?: number,
}

export interface AppointmentExtProp {
  OFFER_AMOUNT: number | null;
  OFFER_CURRENCY: string | null;
}

export interface OfferExtProp {
  OFFER_AMOUNT: number | null;
  OFFER_CURRENCY: string | null;
  OFFER_NUMBER: string | null;
}

export interface SelectedComposerOffers {
  [key: string]: boolean;
}

export interface OrderExtProp {
  ORDER_AMOUNT: number | null;
  ORDER_CURRENCY: string | null;
  ORDER_NUMBER: number | null;
}

export interface ExtendedProperties {
  PLANER_CODE?: string;
  OFFER_AMOUNT?: number;
  OFFER_CURRENCY?: string;
  OFFER_NUMBER?: string;
  ORDER_AMOUNT?: number;
  ORDER_CURRENCY?: string;
  ORDER_NUMBER?: number;
  CUSTOMER?: string;
  COMPOSER_CUSTOMER?: string;
}

export interface ConstructionTypes {
  Id: number;
  Name: string;
  Inactive: boolean;
  Description: string;
}

export interface EmailData {
  "KKZ": "string",
  "Name": "string",
  "Email": "string",
  "LogoUrl": "string",
  "Address": {
    "CountryCode": "string",
    "ZipCode": "string",
    "Address": "string",
    "AddressExt": "string",
    "City": "string",
    "Email": "string",
    "Phone": "string",
    "PhonePrefix": "string",
    "Mobile": "string",
    "MobilePrefix": "string",
    "ProvinceCode": "string",
    "Website": "string"
  },
  "ContactManager": {
    "Id": 0,
    "Login": "string",
    "FirstName": "string",
    "LastName": "string",
    "Organisation": "string",
    "Email": "string",
    "Phone": "string",
    "Mobile": "string",
    "LanguageCode": "string",
    "AvatarURL": "string",
    "Available": true,
    "Inactive": true,
    "NotificationsEnabled": true,
    "AppointmentMailEnabled": true,
    "LeadMailEnabled": true,
    "EnableReporting": true,
    "LastActivity": "2023-01-26T11:29:53.546Z",
    "Gender": "string"
  },
  "AreaManager": {
    "Id": 0,
    "Login": "string",
    "FirstName": "string",
    "LastName": "string",
    "Organisation": "string",
    "Email": "string",
    "Phone": "string",
    "Mobile": "string",
    "LanguageCode": "string",
    "AvatarURL": "string",
    "Available": true,
    "Inactive": true,
    "NotificationsEnabled": true,
    "AppointmentMailEnabled": true,
    "LeadMailEnabled": true,
    "EnableReporting": true,
    "LastActivity": "2023-01-26T11:29:53.546Z",
    "Gender": "string"
  },
  "InternalForwardAvailable": true
}

export interface Broschures {
  key: string;
  title: string;
  img: string;
  ordernumber: string;
}

export interface Ausschreibungstextes {
  key: string;
  title: string;
  img: string;
  ordernumber: string;
}

export interface CadDatens {
  key: string;
  title: string;
  img: string;
  ordernumber: string;
}

export interface Musters {
  key: string;
  title: string;
  img: string;
}

export interface Keyword {
  Owned: boolean;
  Text: string;
}

export type leadSortType =
  | 'LAST_ACTION'
  | 'ID'
  | 'TYPE'
  | 'CONTACT'
  | 'STATUS'
  | 'RESPONSIBLE'
  | 'DATE'
  | 'DATE2'
  | 'LAND'
  | 'COMMISSION';

export type FatcsStatus = 'COMPENDIUM_ACCESS' | 'COMPENDIUM_DENIED' | 'COMPENDIUM_REQUESTED';

export interface Articles {
  Article: string;
  Description: string;
  Id?: number;

}

export interface ImportComposerLeadRequest {
  LeadTypeId: number;
  LeadSourceId: number;
  ContactTypeId: number;
  LeadDate: string;
  LeadText: string;
}

export interface ImportComposerLeadResponse {
  Number: string;
}

export interface ProfileStudio {
  KKZ: string,
  Name: string,
  Address: string,
  PLZ: string,
  City: string,
  Email: string,
  Area: number,
  CertificationIFT: boolean,
  CountryText: string;
  Country: string,
  Mandant: AdminMandant,
}

export interface EditLeadDialogData {
  leadId: number;
  /**
   * When false the dialog is opened in "share only" mode: every header field is read only
   * and the only thing that can be changed is the "share with Finstral" option.
   */
  canEditFields: boolean;
  tags: Keyword[];
  broschures: Broschures[];
  ausschreibungstextes: Ausschreibungstextes[];
  cadDatens: CadDatens[];
  musters: Musters[];
}

export interface LeadsQueryFilter {
  OnlyMine?: boolean;
  OnlyUnread?: boolean;
  OnlyPremiumPartner?: boolean;
  OnlyRequiresConfirmation?: boolean;
  ShowOpen?: boolean;
  ShowClosed?: boolean;
  HideExcluded?: boolean;
  HideGreenDeadline?: boolean;
  HideOrangeDeadline?: boolean;
  HideRedDeadline?: boolean;
  FromDateCreated?: string;
  ToDateCreated?: string;
  FromDateClosed?: string;
  ToDateClosed?: string;
  LeadTypeIds?: number[];
  ContactIds?: number[];
  ContactTypeIds?: number[];
  LeadStateIds?: number[];
  CountryCodes?: string[];
}

export interface LeadsQuerySearch {
  SearchText: string;
  SearchTypes: LeadsQuerySearchTypes;
}

export interface LeadsQueryRequest {
  Searches?: LeadsQuerySearch[];
  Filter?: LeadsQueryFilter;
  Sorting?: string[];
  UserId?: number;
  PageSize?: number;
  PageNumber?: number;
  Format?: string; // For download
}

export enum LeadsQuerySearchTypes {
  NONE = 0,
  LEAD = 1, // ??
  CONTACT = 2,
  ADDRESS = 4,
  DOCUMENT = 8,
  HISTORY = 16,
  EDITOR = 256,
  ARTICLE = 32, // ??
  LEADNUMBER = 64,
  KEYWORDS = 128,
}
